import React, { useEffect, useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { ScrollArea } from '../ui/scroll-area';
import { Plus, FileText, Loader2, Sparkles, GitBranch, History, ChevronRight } from 'lucide-react';
import { tl } from '../../lib/testingLabApi';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import DocumentationDiffViewer from './DocumentationDiffViewer';

export const DocsTab = () => {
  const [files, setFiles] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [selected, setSelected] = useState(null);
  const [sections, setSections] = useState([]);
  const [versions, setVersions] = useState([]);
  const [content, setContent] = useState('');
  const [editing, setEditing] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showAkroPropose, setShowAkroPropose] = useState(false);
  const [activeProposalId, setActiveProposalId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [activeSectionId, setActiveSectionId] = useState(null);

  const loadFiles = async () => {
    try {
      const data = await tl.listDocFiles();
      setFiles(data.items || []);
    } catch (e) { toast.error(e.message); }
  };
  const loadProposals = async () => {
    try {
      const data = await tl.listProposals();
      setProposals(data.items || []);
    } catch (e) { toast.error(e.message); }
  };

  useEffect(() => { loadFiles(); loadProposals(); }, []);

  const openFile = async (file) => {
    setSelected(file);
    setEditing(false);
    setActiveSectionId(null);
    try {
      const [details, secs, vers] = await Promise.all([
        tl.getDocFile(file.documentation_file_id),
        tl.listDocSections(file.documentation_file_id),
        tl.listDocVersions(file.documentation_file_id),
      ]);
      setContent(details.current_version?.content_snapshot || '');
      setSections(secs.items || []);
      setVersions(vers.items || []);
    } catch (e) { toast.error(e.message); }
  };

  const createFile = async (body) => {
    setBusy(true);
    try {
      const f = await tl.createDocFile(body);
      setShowCreate(false);
      await loadFiles();
      await openFile(f);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const saveContent = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await tl.updateDocContent(selected.documentation_file_id, { content, change_summary: 'Direct edit' });
      toast.success('Saved new version');
      await openFile(selected);
      setEditing(false);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const askAkroPropose = async (instruction) => {
    if (!selected) return;
    setBusy(true);
    try {
      const prop = await tl.akroDocProposal({
        documentation_file_id: selected.documentation_file_id,
        section_id: activeSectionId,
        instruction,
      });
      toast.success('Akro proposed an edit');
      setShowAkroPropose(false);
      await loadProposals();
      setActiveProposalId(prop.proposal_id);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const fileProposals = selected
    ? proposals.filter((p) => p.documentation_file_id === selected.documentation_file_id)
    : [];

  return (
    <div className="grid grid-cols-12 gap-4 h-[calc(100vh-220px)]" data-testid="docs-tab">
      <div className="col-span-3">
        <Card className="h-full flex flex-col">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <h3 className="text-sm font-semibold">Documentation</h3>
            <Button size="sm" onClick={() => setShowCreate(true)} data-testid="doc-new"><Plus className="w-3 h-3" /></Button>
          </div>
          <ScrollArea className="flex-1">
            <div className="p-2 space-y-1">
              {files.map((f) => (
                <button key={f.documentation_file_id} onClick={() => openFile(f)}
                  className={cn(
                    'w-full text-left p-2 rounded text-xs flex items-center gap-2',
                    selected?.documentation_file_id === f.documentation_file_id
                      ? 'bg-violet-500/10 border border-violet-500'
                      : 'hover:bg-accent'
                  )}
                  data-testid={`doc-row-${f.documentation_file_id}`}>
                  <FileText className="w-3 h-3 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{f.title}</div>
                    <div className="text-[10px] text-muted-foreground truncate">{f.file_path}</div>
                  </div>
                </button>
              ))}
              {files.length === 0 && (
                <div className="text-center py-8 text-xs text-muted-foreground">No docs yet</div>
              )}
            </div>
          </ScrollArea>
          {fileProposals.length > 0 && (
            <div className="border-t border-border p-2 max-h-40 overflow-auto">
              <div className="text-[10px] font-semibold text-muted-foreground uppercase mb-1.5">Proposals</div>
              {fileProposals.slice(0, 5).map((p) => (
                <button key={p.proposal_id}
                  onClick={() => setActiveProposalId(p.proposal_id)}
                  className="w-full text-left text-[11px] p-1.5 rounded hover:bg-accent flex items-center gap-1.5"
                  data-testid={`proposal-row-${p.proposal_id}`}>
                  <GitBranch className="w-3 h-3 flex-shrink-0 text-violet-500" />
                  <span className="truncate">{p.reason_for_change || 'Proposal'}</span>
                  <Badge className="text-[8px] h-3 ml-auto" variant="outline">{p.status}</Badge>
                </button>
              ))}
            </div>
          )}
        </Card>
      </div>

      <div className="col-span-6">
        {selected ? (
          <Card className="h-full flex flex-col">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold">{selected.title}</h3>
                <p className="text-[11px] text-muted-foreground">{selected.file_path}</p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setShowAkroPropose(true)} data-testid="akro-propose-doc">
                  <Sparkles className="w-3 h-3 mr-1" /> Akro
                </Button>
                {!editing ? (
                  <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="doc-edit">Edit</Button>
                ) : (
                  <>
                    <Button size="sm" variant="ghost" onClick={() => { setEditing(false); openFile(selected); }}>Cancel</Button>
                    <Button size="sm" onClick={saveContent} disabled={busy} data-testid="doc-save">
                      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
                    </Button>
                  </>
                )}
              </div>
            </div>
            {editing ? (
              <Textarea value={content} onChange={(e) => setContent(e.target.value)}
                className="flex-1 font-mono text-xs rounded-none border-0 resize-none" data-testid="doc-content-edit" />
            ) : (
              <ScrollArea className="flex-1">
                <pre className="p-4 text-xs whitespace-pre-wrap font-mono" data-testid="doc-content-view">{content}</pre>
              </ScrollArea>
            )}
          </Card>
        ) : (
          <Card className="h-full flex items-center justify-center text-muted-foreground text-sm">
            Select a documentation file
          </Card>
        )}
      </div>

      <div className="col-span-3 flex flex-col gap-4">
        {selected && (
          <>
            <Card className="flex-1 flex flex-col">
              <div className="px-3 py-2 border-b border-border">
                <h4 className="text-xs font-semibold">Sections</h4>
              </div>
              <ScrollArea className="flex-1">
                <div className="p-2 space-y-1">
                  {sections.map((s) => (
                    <button key={s.documentation_section_id}
                      onClick={() => setActiveSectionId(s.documentation_section_id)}
                      className={cn(
                        'w-full text-left p-1.5 rounded text-[11px] hover:bg-accent flex items-center gap-1',
                        activeSectionId === s.documentation_section_id && 'bg-violet-500/10'
                      )}
                      style={{ paddingLeft: `${(s.section_level - 1) * 12 + 6}px` }}
                      data-testid={`section-${s.documentation_section_id}`}>
                      <ChevronRight className="w-2.5 h-2.5 flex-shrink-0" />
                      <span className="truncate">{s.heading}</span>
                    </button>
                  ))}
                </div>
              </ScrollArea>
            </Card>
            <Card className="flex-1 flex flex-col">
              <div className="px-3 py-2 border-b border-border flex items-center gap-2">
                <History className="w-3 h-3" />
                <h4 className="text-xs font-semibold">Version history</h4>
              </div>
              <ScrollArea className="flex-1">
                <div className="p-2 space-y-1">
                  {versions.map((v) => (
                    <div key={v.documentation_version_id} className="text-[11px] border-l-2 border-border pl-2 py-0.5">
                      <div className="flex items-center gap-1">
                        <Badge variant="outline" className="text-[9px] h-3.5">v{v.version_number}</Badge>
                        <span className="text-muted-foreground text-[10px]">{new Date(v.created_at).toLocaleString()}</span>
                      </div>
                      <div className="text-[10px] truncate">{v.change_summary || v.source_type}</div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </Card>
          </>
        )}
      </div>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent data-testid="doc-create-dialog">
          <DialogHeader><DialogTitle>New documentation file</DialogTitle></DialogHeader>
          <CreateDocForm onSubmit={createFile} disabled={busy} />
        </DialogContent>
      </Dialog>

      <Dialog open={showAkroPropose} onOpenChange={setShowAkroPropose}>
        <DialogContent data-testid="akro-propose-dialog">
          <DialogHeader><DialogTitle>Ask Akro to propose an edit</DialogTitle></DialogHeader>
          <AkroProposeForm onSubmit={askAkroPropose} disabled={busy} sectionId={activeSectionId} />
        </DialogContent>
      </Dialog>

      <DocumentationDiffViewer
        proposalId={activeProposalId}
        open={!!activeProposalId}
        onClose={() => setActiveProposalId(null)}
        onApplied={async () => { await loadProposals(); if (selected) await openFile(selected); }}
      />
    </div>
  );
};

const CreateDocForm = ({ onSubmit, disabled }) => {
  const [title, setTitle] = useState('');
  const [filePath, setFilePath] = useState('');
  const [module, setModule] = useState('');
  const [initial, setInitial] = useState('');
  return (
    <div className="space-y-3">
      <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (e.g. Finance Module)" data-testid="doc-title-input" />
      <Input value={filePath} onChange={(e) => setFilePath(e.target.value)} placeholder="Path (e.g. docs/finance.md)" data-testid="doc-path-input" />
      <Input value={module} onChange={(e) => setModule(e.target.value)} placeholder="Module (optional)" data-testid="doc-module-input" />
      <Textarea value={initial} onChange={(e) => setInitial(e.target.value)} placeholder="Initial Markdown content (optional)" className="font-mono text-xs min-h-[150px]" data-testid="doc-initial-input" />
      <DialogFooter>
        <Button onClick={() => onSubmit({ title, file_path: filePath, module, initial_content: initial })} disabled={!title || !filePath || disabled} data-testid="doc-create-submit">
          {disabled ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
        </Button>
      </DialogFooter>
    </div>
  );
};

const AkroProposeForm = ({ onSubmit, disabled, sectionId }) => {
  const [instruction, setInstruction] = useState('');
  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        {sectionId ? 'Akro will propose changes to the selected section.' : 'No section selected — Akro will propose changes to the entire file.'}
      </p>
      <Textarea value={instruction} onChange={(e) => setInstruction(e.target.value)}
        placeholder="What should Akro change? (e.g. 'Document the new income type validation rules')"
        className="min-h-[100px] text-xs" data-testid="akro-instruction-input" />
      <DialogFooter>
        <Button onClick={() => onSubmit(instruction)} disabled={!instruction || disabled} data-testid="akro-propose-submit">
          {disabled ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Generate proposal'}
        </Button>
      </DialogFooter>
    </div>
  );
};

export default DocsTab;
